import React, { useState, useRef, useEffect, useCallback } from 'react';

import Button from '@mui/material/Button';
import { Theme } from '@mui/material/styles';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';

import { useEventListener } from '../../utils/hooks';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    wrapper: {
      width: '100%',
      transition: 'max-height 0.5s ease-out',
      overflow: 'hidden',
    },
    viewBtn: {
      width: '100%',
      textTransform: 'none',
      textAlign: 'center',
      fontSize: 16,
      lineHeight: 1,
      padding: 20,
      color: '#B5B1C1',
      background: '#3A354D',
      borderRadius: 5,
      fontWeight: 'bold',
      [theme.breakpoints.up('md')]: {
        fontSize: 18,
      },
      '@media (hover: none)': {
        '&:hover': {
          backgroundColor: '#3A354D',
        },
      },
    },
  }),
);

interface AccordionButtonProps {
  listIsOpen: boolean;
  handleClick: () => void;
  buttonClass?: string;
}

export const AccordionButton: React.FC<AccordionButtonProps> = ({
  listIsOpen,
  handleClick,
  buttonClass = '',
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  return (
    <Button className={cx(classes.viewBtn, buttonClass)} onClick={handleClick}>
      {t(listIsOpen ? 'button.hide' : 'button.viewAll')}
    </Button>
  );
};

interface CustomAccordionProps {
  itemsToShow: number;
  wrapperClass?: string;
  ButtonComponent?: typeof AccordionButton;
  delay?: number;
  buttonClass?: string;
}

const CustomAccordion: React.FC<CustomAccordionProps> = ({
  itemsToShow,
  children,
  wrapperClass = '',
  ButtonComponent = AccordionButton,
  delay = 0,
  buttonClass = '',
}) => {
  const [listIsOpen, setListOpenValue] = useState(false);
  const [hiddenHeight, setHiddenHeight] = useState(0);
  const classes = useStyles();
  const ref = useRef<HTMLDivElement>(null);
  const timeout = useRef<null | ReturnType<typeof setTimeout>>(null);
  const childRef = useRef(new Array(itemsToShow));
  const allElementsLength = children && Array.isArray(children) ? children.length : 0;

  const toggleList = () => {
    setListOpenValue(!listIsOpen);
  };

  const handleSetElemHeight = useCallback(() => {
    if (timeout?.current) {
      clearTimeout(timeout?.current);
    }
    timeout.current = setTimeout(() => {
      if (childRef.current && Array.isArray(childRef.current)) {
        const arr = childRef.current.filter((item) => !!item);
        setHiddenHeight(
          arr.reduce((sum, current, index) => {
            if (current) {
              const styles = window.getComputedStyle(current);
              //check if last displayed el has border to display without border
              if (index === arr.length - 1) {
                const borderBottomWidth = parseInt(styles['border-bottom-width']);
                const currentHeight =
                  borderBottomWidth > 0
                    ? current.offsetHeight - borderBottomWidth
                    : current.offsetHeight + parseInt(styles['margin-bottom']);
                return sum + currentHeight;
              } else {
                return sum + current.offsetHeight + parseInt(styles['margin-bottom']);
              }
            } else {
              return 0;
            }
          }, 0),
        );
      }
    }, delay);
  }, [delay]);

  //add refs to child elements
  const childrenWithProps = React.Children.map(children, (child, index) => {
    //@ts-ignore
    return React.cloneElement(child, {
      ref: (el) => {
        if (index < itemsToShow) childRef.current[index] = el;
      },
    });
  });

  useEffect(() => {
    handleSetElemHeight();
  }, [handleSetElemHeight, itemsToShow, children]);

  useEffect(() => {
    return () => {
      if (timeout?.current) {
        clearTimeout(timeout?.current);
      }
    };
  }, []);

  //event listener on resize to recalculate if elements height changed
  useEventListener('resize', handleSetElemHeight);

  return (
    <>
      <div
        className={cx(classes.wrapper, wrapperClass)}
        ref={ref}
        style={{
          maxHeight: listIsOpen ? ref?.current?.scrollHeight || 0 : hiddenHeight,
        }}
      >
        {childrenWithProps}
      </div>
      {allElementsLength > itemsToShow && (
        <ButtonComponent
          buttonClass={buttonClass}
          listIsOpen={listIsOpen}
          handleClick={toggleList}
        />
      )}
    </>
  );
};

export default CustomAccordion;
